import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createEPCRating } from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import { clearKeys } from '../../../utils/objectUtils';
import SingleSelect from '../../formElements/SingleSelect';
import FormActions from '../../ui/formActions/FormActions';

import { validate } from './validateEnvironment';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const message = 'Participant EPC rating has been added';

const CreateEPCRating = ({ onClose, disabled }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: null,
        epcRatingId: '',
        participantId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [keys, setKeys] = useState({ epcRatingId: '0' });
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const epcRatingsDetails = useSelector(
        (state) => state.entities.directusService.epcRatingsDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!currentParticipant?.id) return;
        setNewEntry((prev) => ({ ...prev, participantId: currentParticipant.id }));
    }, [currentParticipant]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === message || errorMessage) clearForm();
    }, [successMessage, errorMessage]);

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setIsButtonDisabled(false);
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onFormExit = () => {
        clearForm();
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, 'create');
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(createEPCRating(newEntry, message));
        onFormExit();
    };

    // RENDER
    return (
        <form onSubmit={onSubmit} data-testid="form_start_create_epc_rating">
            <h2 className={app.sectionHeading}>Create EPC Rating</h2>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <SingleSelect
                        id="epcRatingId"
                        key={keys.epcRatingId}
                        label="EPC Rating"
                        placeholder="Select EPC rating..."
                        disabled={disabled}
                        mandatory={true}
                        menuItems={epcRatingsDetails || []}
                        selectedId={newEntry.epcRatingId || ''}
                        selected={getObjFromId(epcRatingsDetails, newEntry.epcRatingId)}
                        error={errors.epcRatingId}
                        onChange={(chosenId) => onUpdate('epcRatingId', chosenId)}
                    />
                </div>
                <div className={form.formColumn}></div>
            </div>
            <FormActions
                onClose={onSubmit}
                onCancel={onFormExit}
                btnText="Add EPC Rating"
                disabled={disabled || isButtonDisabled}
            />
        </form>
    );
};

CreateEPCRating.propTypes = {
    onClose: PropTypes.func,
    disabled: PropTypes.bool
};

export default CreateEPCRating;
